import { FC, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge, GoBack } from '@/_shared';
import { WizardNavigationButton } from '@/_shared/components/Wizard/types';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getContentDetailsCall } from '@/training/hardSkills/state/api';
import { postQuizByIdNextCall } from '../../state/api';
import { DialogConfirmExitFromQuiz } from '../DialogConfirmExitFromQuiz';
import { DynamicQuiz } from './DynamicQuiz';
import { useEmployeeCompleteQuiz } from './hook';

type Props = { wizardNavigationButton: WizardNavigationButton };

export const EmployeeCompleteQuiz: FC<Props> = ({ wizardNavigationButton }) => {
  const t = useTranslations('quiz.dynamicQuiz');
  const tShared = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { openDialog } = useDialog('confirmExitFromQuizDialog');
  const { error: errorPostQuizByIdNext } = postQuizByIdNextCall.hook();

  const gapXXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxxl')}`) || 64;
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 12;

  const { currentStep, control, currentStepInPreview, progressiveSteps } =
    useEmployeeCompleteQuiz();
  const { response: responseGetContentDetails } = getContentDetailsCall.hook();

  const progressPercentage = useMemo(() => {
    const stepNumber = currentStep?.nextQuestion?.stepNumber ?? 0;
    const totalSteps = currentStep?.nextQuestion?.totalSteps ?? 0;
    // When quiz finishes, nextQuestion is undefined so stepNumber & totalSteps are 0
    // In that case, show 100% if we're in preview (showing feedback for the last question)
    if (totalSteps === 0) {
      return currentStepInPreview ? 100 : 0;
    }
    const completedSteps = stepNumber - 1;
    return Math.min(100, (completedSteps / totalSteps) * 100);
  }, [
    currentStep?.nextQuestion?.stepNumber,
    currentStep?.nextQuestion?.totalSteps,
    currentStepInPreview,
  ]);

  return (
    <>
      {/* Progress bar */}
      <Box
        position='absolute'
        left={0}
        right={0}
        width='100%'
        height='xxs'
        backgroundColor='transparent'
        borderRadius='xxl'
      >
        <Box
          height='xxs'
          backgroundColor='primary'
          borderRadius='xxl'
          style={{
            width: `${progressPercentage}%`,
            transition: 'width 0.3s ease-in-out',
          }}
        />
      </Box>
      <Box mt='m'>
        <GoBack label={t('exit')} onBack={openDialog} />
        <DialogConfirmExitFromQuiz />
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          alignItems='flex-start'
          justifyContent='center'
          py={genericResponsiveValue({
            breakPoints: { desktop: 'xl' },
            defaultValue: 'm',
          })}
          width='100%'
          style={{ gap: gapXXXL }}
          flex={10}
        >
          <Box
            flex={genericResponsiveValue({
              breakPoints: { desktop: 6 },
              defaultValue: 10,
            })}
            marginY={genericResponsiveValue({
              breakPoints: { desktop: 'auto' },
              defaultValue: undefined,
            })}
          >
            <Typography
              variant='h2'
              fontSize={genericResponsiveValue({
                breakPoints: { desktop: 50 as never },
                defaultValue: 'xxxl',
              })}
              lineHeight={genericResponsiveValue({
                breakPoints: { desktop: '60px' as never },
                defaultValue: 'xxxl',
              })}
              fontWeight='bold'
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'm' },
                defaultValue: 'xs',
              })}
            >
              {t('quizTitle', { quizName: responseGetContentDetails?.title })}
            </Typography>
            <Typography variant='p1/light' color='grey/light'>
              {responseGetContentDetails?.summary}
            </Typography>
          </Box>
          <Box
            flex={genericResponsiveValue({
              breakPoints: { desktop: 5 },
              defaultValue: 10,
            })}
            width='100%'
          >
            <Card>
              {errorPostQuizByIdNext ? (
                <AlertBadge
                  iconName='info'
                  type='error'
                  label={tShared('errors.genericApiError')}
                />
              ) : (
                <DynamicQuiz
                  currentStep={currentStep}
                  control={control}
                  currentStepInPreview={currentStepInPreview}
                  progressiveSteps={progressiveSteps}
                />
              )}
            </Card>
          </Box>
        </Box>
        <Box mt='xs'>
          <Box
            display='flex'
            justifyContent={genericResponsiveValue({
              breakPoints: { desktop: 'flex-end' },
              defaultValue: 'flex-start',
            })}
            style={{ gap: gapXS }}
          >
            {currentStep?.nextQuestion?.stepNumber &&
              currentStep?.nextQuestion?.stepNumber >= 2 && <wizardNavigationButton.Previous />}
            <wizardNavigationButton.Next />
          </Box>
        </Box>
      </Box>
    </>
  );
};
